import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import ApproveTable from "../../components/Table/ApproveTable";
import {
  formatIsoDate,
  renderStatus,
  formatName,
  formatPositionLabel,
  toUiPosition,
} from "../../utils/generalUtils";
import { Button, Select, Space } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import TableSkeleton from "../../components/Loading/TableSkeleton";
import usePdfBlob from "../../hooks/usePdfBlob";
import PdfPreviewer from "../../components/PdfPreviewer";

const ApproveFsr = () => {
  const { response, loading, fetchData } = useAxios();
  // const { approverFsrPopup } = useSelector((state) => state.generalSlice);
  const approverFsrPopup = useSelector(
    (state) => state.generalSlice.approverFsrPopup,
  );
  const { positions, id: employee_id } = useSelector(
    (state) => state.accountDetailSlice.details,
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/fsr/${positionId}`, method: "GET" });
  };

  const mappedEmpPos = positions
    .filter((item) => item?.is_approver)
    .map((item) => {
      const normalized = toUiPosition(item);

      return {
        value: normalized?.position_id,
        label: formatPositionLabel(normalized),
        disabled: normalized?.is_inactive,
      };
    });
  const activeEmpPos = mappedEmpPos.filter((item) => !item.disabled);
  const hasActiveApproverPosition = activeEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    activeEmpPos.length > 0 ? activeEmpPos[0].value : undefined,
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      activeEmpPos.length > 0
    ) {
      setSelectedPos(activeEmpPos[0].value);
    }
  }, [activeEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut",
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut",
      ),
      ...item?.submission?.faculty_service_record,
      employee_id: item?.submission?.employee_id,
      position_id: item?.submission?.position_id,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      submitter_position: item?.submission?.position?.position_name,
      remarks: item?.remarks || null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key",
      rowScope: "row",
      width: 60,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Academic Term",
      dataIndex: "academic_term",
      key: "academic_term",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (value) => (value ? value : "—"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return <Button icon={<EyeOutlined />} type="primary" shape="circle" />;
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "date_submitted",
      key: "date_submitted",
    },
  ];

  //this is optional if there is a additional form other than approving and disapproving just like remarks
  const addtnlfield = [
    {
      name: "remarks",
      label: "Send Remarks",
      placeholder: "Enter remarks",
    },
  ];

  const FsrPdfViewer = ({ data }) => {
    const { pdfBlobUrl, loading } = usePdfBlob({
      endpoint: "/fsr/pdf/snapshot",
      params: {
        submission_id: data.submission_id,
      },
      enabled: !!data?.submission_id,
    });

    return (
      <PdfPreviewer pdfBlobUrl={pdfBlobUrl} loading={loading} height="400px" />
    );
  };

  return (
    <>
      {loading ? (
        <TableSkeleton tableType="approve" />
      ) : (
        <>
          <Space>
            <p>Approve as: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveApproverPosition}
            />
          </Space>
          {!hasActiveApproverPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active approver position available.
            </p>
          )}

          <ApproveTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={approverFsrPopup}
            popupName="approverFsrPopup"
            popupWidth={1000}
            docuType="Faculty Service Record"
            addtnlFormFields={addtnlfield}
            addtnlContent={(data) => <FsrPdfViewer data={data} />}
            approverId={employee_id}
            approverposId={selectedPos}
            onRefresh={() => {
              if (selectedPos !== undefined && selectedPos !== null) {
                handleRefresh(selectedPos);
              }
            }}
          />
        </>
      )}
    </>
  );
};

export default ApproveFsr;
